import { useState, useEffect } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { Send, AlertCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { useCreditReservation } from '@/hooks/useCreditReservation';
import { useRateLimit } from '@/hooks/useRateLimit';
import { CampaignFormData } from '@/types/campaignForm';
import { EditCandidateModal } from '../EditCandidateModal';
import { BuyCreditModal } from '@/components/credits/BuyCreditModal';
import { validateCandidateEmailFormat } from '@/utils/emailValidation';
import { validateAndFormatPhone } from '@/utils/phoneValidation';

interface Step4ConfirmationProps {
  form: UseFormReturn<CampaignFormData>;
  aiAnalysisData: any;
  onPrevious: () => void;
  onCancel: () => void;
  onSaveDraft: () => void;
  onSubmit: (data: CampaignFormData) => void;
  isSubmitting: boolean;
  isDraft: boolean;
  submissionStep?: 'idle' | 'validating' | 'updating' | 'finalizing' | 'sending_email' | 'complete';
  campaignId: string | null;
}

export const Step4Confirmation = ({
  form,
  aiAnalysisData: _aiAnalysisData,
  onPrevious,
  onCancel: _onCancel,
  onSaveDraft: _onSaveDraft,
  onSubmit,
  isSubmitting,
  isDraft: _isDraft,
  submissionStep = 'idle',
  campaignId,
}: Step4ConfirmationProps) => {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isBuyCreditModalOpen, setIsBuyCreditModalOpen] = useState(false);
  const [reservationStatus, setReservationStatus] = useState<{
    checking: boolean;
    hasValidReservation: boolean;
    expiresAt?: string;
    error?: string;
  }>({
    checking: true,
    hasValidReservation: false,
  });

  const { toast } = useToast();
  const { profile } = useAuth();
  const { checkAndRenewReservation, reserving } = useCreditReservation();
  const { checkRateLimit, isChecking: isCheckingRateLimit } = useRateLimit();
  
  // Utiliser watch pour avoir les valeurs réactives
  const formData = form.watch();

  /**
   * Vérifier et renouveler la réservation au chargement du Step 4
   * Requirements: 1.5, 3.2
   */
  useEffect(() => {
    const checkReservation = async () => {
      // Vérifier que nous avons les informations nécessaires
      if (!campaignId || !profile?.organization_id) {
        console.warn('⚠️ Step4 - Informations manquantes:', { campaignId, organizationId: profile?.organization_id });
        setReservationStatus({
          checking: false,
          hasValidReservation: false,
          error: 'Informations de campagne manquantes'
        });
        return;
      }

      console.warn('🔍 Step4 - Vérification de la réservation:', { campaignId, organizationId: profile.organization_id });
      setReservationStatus(prev => ({ ...prev, checking: true }));

      try {
        const result = await checkAndRenewReservation(profile.organization_id, campaignId);

        console.warn('✅ Step4 - Résultat vérification:', result);

        if (result.success && result.hasValidReservation) {
          // Réservation valide trouvée ou créée - silencieux, pas de notification
          setReservationStatus({
            checking: false,
            hasValidReservation: true,
          });
        } else if (result.needsCredits) {
          // Pas de crédits disponibles - afficher la modale d'achat
          console.warn('💳 Step4 - Crédits insuffisants, affichage modale d\'achat');
          setReservationStatus({
            checking: false,
            hasValidReservation: false,
            error: 'Crédits insuffisants'
          });

          setIsBuyCreditModalOpen(true);

          toast({
            title: "💳 Crédits insuffisants",
            description: "Vous devez acheter des crédits pour lancer cette campagne",
            variant: "destructive",
          });
        } else {
          // Autre erreur
          setReservationStatus({
            checking: false,
            hasValidReservation: false,
            error: result.error || 'Erreur inconnue'
          });

          toast({
            title: "❌ Erreur de réservation",
            description: result.error || "Impossible de réserver un crédit",
            variant: "destructive",
          });
        }
      } catch (error) {
        console.error('❌ Step4 - Erreur lors de la vérification:', error);
        setReservationStatus({
          checking: false,
          hasValidReservation: false,
          error: error instanceof Error ? error.message : 'Erreur inconnue'
        });

        toast({
          title: "❌ Erreur",
          description: "Une erreur est survenue lors de la vérification des crédits",
          variant: "destructive",
        });
      }
    };

    checkReservation();
  }, [campaignId, profile?.organization_id, checkAndRenewReservation, toast]);

  const validateBeforeSubmit = (): boolean => {
    const formData = form.getValues();
    
    // Vérifications critiques avec messages d'erreur spécifiques
    if (!formData.candidate_name?.trim()) {
      toast({
        title: "❌ Informations manquantes",
        description: "Le nom du candidat est requis",
        variant: "destructive",
      });
      return false;
    }

    if (!formData.candidate_email?.trim()) {
      toast({
        title: "❌ Informations manquantes", 
        description: "L'email du candidat est requis",
        variant: "destructive",
      });
      return false;
    }

    // Validation du format email
    if (!validateCandidateEmailFormat(formData.candidate_email)) {
      toast({
        title: "❌ Email invalide",
        description: "Le format de l'email du candidat est invalide. Veuillez le corriger.",
        variant: "destructive",
      });
      return false;
    }

    // VALIDATION TÉLÉPHONE DÉSACTIVÉE - Pour réactiver, décommenter le bloc ci-dessous
    /*
    if (formData.candidate_phone?.trim()) {
      const phoneValidation = validateAndFormatPhone(formData.candidate_phone);
      if (!phoneValidation.isValid) {
        toast({
          title: "❌ Téléphone invalide",
          description: phoneValidation.error || "Le format du numéro de téléphone est invalide. Utilisez le format +33612345678",
          variant: "destructive",
        });
        return false;
      }
    }
    */

    if (!formData.candidate_cv_url) {
      toast({
        title: "❌ Document manquant",
        description: "Le CV du candidat doit être téléchargé",
        variant: "destructive",
      });
      return false;
    }

    if (!formData.job_description_text?.trim() && !formData.job_description_file_url) {
      toast({
        title: "❌ Description manquante",
        description: "Une description du poste (texte ou fichier) est requise",
        variant: "destructive",
      });
      return false;
    }

    if (!formData.selected_evaluation_criteria || formData.selected_evaluation_criteria.length < 5) {
      toast({
        title: "❌ Critères insuffisants",
        description: `Minimum 5 critères requis (actuellement: ${formData.selected_evaluation_criteria?.length || 0})`,
        variant: "destructive",
      });
      return false;
    }

    if (formData.selected_evaluation_criteria.length > 10) {
      toast({
        title: "❌ Trop de critères",
        description: `Maximum 10 critères autorisés (actuellement: ${formData.selected_evaluation_criteria.length})`,
        variant: "destructive",
      });
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    console.warn('🎯 DEBUG Step4 - handleSubmit appelé');

    // Validation pré-soumission
    if (!validateBeforeSubmit()) {
      console.warn('❌ DEBUG Step4 - Validation pré-soumission échouée');
      return;
    }

    // Déclencher une validation complète du formulaire
    const isValid = await form.trigger();
    console.warn('🎯 DEBUG Step4 - Validation:', isValid);

    if (!isValid) {
      console.warn('❌ DEBUG Step4 - Validation échouée, erreurs:', form.formState.errors);
      console.warn('❌ DEBUG Step4 - Erreurs détaillées:', JSON.stringify(form.formState.errors, null, 2));
      
      // Afficher les erreurs spécifiques
      Object.entries(form.formState.errors).forEach(([field, error]) => {
        console.warn(`❌ Erreur sur ${field}:`, error);
      });
      
      toast({
        title: "❌ Erreurs de validation",
        description: "Veuillez corriger les erreurs dans le formulaire",
        variant: "destructive",
      });
      return;
    }

    // Vérifier le rate limit avant de lancer la campagne
    // Requirements: 13.1, 13.2, 13.3
    console.warn('🚦 DEBUG Step4 - Vérification du rate limit...');
    const rateLimitResult = await checkRateLimit('campaign_launch', 10);
    
    if (!rateLimitResult.allowed) {
      console.warn('❌ DEBUG Step4 - Rate limit dépassé:', rateLimitResult);
      
      // Le toast est déjà affiché par le hook useRateLimit
      // Pas besoin d'afficher un toast supplémentaire
      return;
    }

    console.warn('✅ DEBUG Step4 - Rate limit OK, tentatives restantes:', rateLimitResult.remainingAttempts);

    const formData = form.getValues();
    console.warn('🎯 DEBUG Step4 - Données à soumettre:', {
      candidateName: formData.candidate_name,
      candidateEmail: formData.candidate_email,
      criteriaCount: formData.selected_evaluation_criteria?.length,
      hasCV: !!formData.candidate_cv_url,
      jobDescriptionText: formData.job_description_text,
      jobDescriptionFile: formData.job_description_file_url,
      jobDescriptionTextLength: formData.job_description_text?.length || 0
    });

    // Appeler directement onSubmit avec les données du formulaire
    console.warn('🎯 DEBUG Step4 - Appel onSubmit...');
    onSubmit(formData);
  };

  const handleSaveCandidateInfo = (data: { candidate_name: string; candidate_email: string; candidate_phone?: string; target_position: string }) => {
    // Mettre à jour les valeurs du formulaire
    form.setValue('candidate_name', data.candidate_name);
    form.setValue('candidate_email', data.candidate_email);
    // Toujours mettre à jour le téléphone, même s'il est vide
    form.setValue('candidate_phone', data.candidate_phone || '');
    form.setValue('target_position', data.target_position);
  };

  return (
    <div className="w-full flex flex-col items-center gap-6">
      {/* Section Header */}
      <div className="space-y-2 text-center max-w-2xl">
        <h1 className="text-gray-800 text-2xl font-semibold font-['Bricolage_Grotesque']">
          Résumé de la campagne
        </h1>
        <p className="text-neutral-400 text-base font-medium">
          Vérifiez toutes les informations avant de lancer votre campagne.
        </p>
      </div>

      {/* Indicateur discret uniquement si crédits insuffisants */}
      {reservationStatus.error === 'Crédits insuffisants' && (
        <div className="w-full max-w-4xl">
          <div className="p-4 bg-red-50 rounded-[10px] border border-red-200">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-red-600" />
              <div className="flex-1">
                <div className="text-red-800 text-sm font-medium font-['Bricolage_Grotesque']">
                  💳 Crédits insuffisants
                </div>
                <div className="text-red-600 text-xs font-normal font-['Bricolage_Grotesque'] mt-1">
                  Vous devez acheter des crédits pour lancer cette campagne
                </div>
              </div>
              <Button
                onClick={() => setIsBuyCreditModalOpen(true)}
                className="px-4 py-2 bg-teal-400 rounded-lg text-white text-sm font-medium font-['Bricolage_Grotesque'] hover:bg-teal-500"
              >
                Acheter des crédits
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Informations sur le candidat */}
      <div className="w-full max-w-4xl flex flex-col justify-center items-center gap-4">
        <div className="self-stretch rounded-[10px] outline outline-1 outline-offset-[-1px] outline-neutral-400/20 flex flex-col justify-start items-start overflow-hidden">
          <div className="self-stretch p-4 bg-gray-50 border-b border-neutral-400/20 inline-flex justify-between items-center">
            <div className="justify-start text-gray-800 text-xl font-medium font-['Bricolage_Grotesque']">
              Informations sur le candidat
            </div>
            <button
              onClick={() => setIsEditModalOpen(true)}
              className="px-2 py-[5px] rounded-lg outline outline-1 outline-offset-[-1px] outline-teal-400 flex justify-center items-center gap-1 hover:bg-teal-50 transition-colors cursor-pointer"
            >
              <div data-svg-wrapper className="relative">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M7.73463 2.09996L2.94546 7.16913C2.76463 7.36163 2.58963 7.7408 2.55463 8.0033L2.3388 9.8933C2.26296 10.5758 2.75296 11.0425 3.42963 10.9258L5.30796 10.605C5.57046 10.5583 5.93796 10.3658 6.11879 10.1675L10.908 5.0983C11.7363 4.2233 12.1096 3.2258 10.8205 2.00663C9.53713 0.79913 8.56296 1.22496 7.73463 2.09996Z" stroke="#40E0D0" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M6.93555 2.9458C7.18638 4.5558 8.49305 5.78663 10.1147 5.94997" stroke="#40E0D0" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M1.75 12.8333H12.25" stroke="#40E0D0" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <div className="justify-start text-teal-400 text-xs font-medium font-['Bricolage_Grotesque'] capitalize tracking-tight">
                Modifier
              </div>
            </button>
          </div>
          <div className="w-[625px] px-4 py-5 flex flex-col justify-start items-start gap-5">
            <div className="flex flex-col justify-start items-start gap-5">
              <div className="inline-flex justify-start items-center gap-2">
                <div className="size-9 p-2 bg-teal-400/10 rounded-lg inline-flex flex-col justify-center items-center gap-2.5 overflow-hidden">
                  <div data-svg-wrapper className="relative">
                    <svg width="20" height="21" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M10.1331 9.55841C10.0498 9.55008 9.9498 9.55008 9.85814 9.55841C7.8748 9.49175 6.2998 7.86675 6.2998 5.86675C6.2998 3.82508 7.9498 2.16675 9.9998 2.16675C12.0415 2.16675 13.6998 3.82508 13.6998 5.86675C13.6915 7.86675 12.1165 9.49175 10.1331 9.55841Z" stroke="#40E0D0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M5.9666 12.6333C3.94993 13.9833 3.94993 16.1833 5.9666 17.5249C8.25827 19.0583 12.0166 19.0583 14.3083 17.5249C16.3249 16.1749 16.3249 13.9749 14.3083 12.6333C12.0249 11.1083 8.2666 11.1083 5.9666 12.6333Z" stroke="#40E0D0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                </div>
                <div className="inline-flex flex-col justify-start items-start gap-px">
                  <div className="justify-start text-gray-800 text-base font-medium font-['Bricolage_Grotesque'] whitespace-nowrap">
                    {formData.candidate_name}
                  </div>
                  <div className="self-stretch justify-start text-neutral-400 text-sm font-normal font-['Bricolage_Grotesque']">
                    Nom
                  </div>
                </div>
              </div>
              <div className="inline-flex justify-start items-center gap-2">
                <div className="size-9 p-2 bg-yellow-400/10 rounded-lg inline-flex flex-col justify-center items-center gap-2.5 overflow-hidden">
                  <div data-svg-wrapper className="relative">
                    <svg width="20" height="21" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M14.167 17.5834H5.83366C3.33366 17.5834 1.66699 16.3334 1.66699 13.4167V7.58341C1.66699 4.66675 3.33366 3.41675 5.83366 3.41675H14.167C16.667 3.41675 18.3337 4.66675 18.3337 7.58341V13.4167C18.3337 16.3334 16.667 17.5834 14.167 17.5834Z" stroke="#FFC506" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M14.1663 8L11.558 10.0833C10.6997 10.7667 9.29134 10.7667 8.433 10.0833L5.83301 8" stroke="#FFC506" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                </div>
                <div className="inline-flex flex-col justify-start items-start gap-px">
                  <div className="justify-start text-gray-800 text-base font-medium font-['Bricolage_Grotesque']">
                    {formData.candidate_email}
                  </div>
                  <div className="self-stretch justify-start text-neutral-400 text-sm font-normal font-['Bricolage_Grotesque']">
                    E-mail
                  </div>
                </div>
              </div>
              {/* CHAMP TÉLÉPHONE DÉSACTIVÉ - Pour réactiver, décommenter le bloc ci-dessous 
              <div className="inline-flex justify-start items-center gap-2">
                <div className="size-9 p-2 bg-blue-400/10 rounded-lg inline-flex flex-col justify-center items-center gap-2.5 overflow-hidden">
                  <div data-svg-wrapper className="relative">
                    <svg width="20" height="21" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M18.3087 15.7751C18.3087 16.0584 18.242 16.3501 18.1003 16.6334C17.9587 16.9167 17.7753 17.1834 17.5337 17.4334C17.1337 17.8584 16.6920 18.1667 16.192 18.3667C15.7003 18.5667 15.167 18.6667 14.592 18.6667C13.7587 18.6667 12.8587 18.4751 11.9003 18.0834C10.942 17.6917 9.98366 17.1584 9.03366 16.4834C8.07533 15.8001 7.16699 15.0334 6.29699 14.1751C5.43533 13.3084 4.66699 12.4001 3.99199 11.4501C3.32533 10.5001 2.79199 9.55008 2.40866 8.60841C2.02533 7.65841 1.83366 6.75841 1.83366 5.90841C1.83366 5.35008 1.92533 4.81675 2.10866 4.33341C2.29199 3.84175 2.58366 3.39175 2.99199 2.99175C3.49199 2.47508 4.04199 2.22508 4.62533 2.22508C4.85866 2.22508 5.09199 2.27508 5.30033 2.37508C5.51699 2.47508 5.70866 2.62508 5.85866 2.84175L7.82533 5.65841C7.97533 5.86675 8.08366 6.05841 8.15866 6.24175C8.23366 6.41675 8.27533 6.59175 8.27533 6.75008C8.27533 6.95008 8.21699 7.15008 8.10033 7.34175C7.99199 7.53341 7.83366 7.73341 7.63366 7.93341L6.99199 8.60008C6.90033 8.69175 6.85866 8.80008 6.85866 8.93341C6.85866 9.00008 6.86699 9.05841 6.88366 9.12508C6.90866 9.19175 6.93366 9.24175 6.95033 9.29175C7.10033 9.57508 7.36699 9.94175 7.75033 10.3834C8.14199 10.8251 8.55866 11.2751 9.00866 11.7251C9.46699 12.1751 9.90866 12.6001 10.3587 12.9917C10.8003 13.3751 11.167 13.6334 11.4587 13.7834C11.5003 13.8001 11.5503 13.8251 11.6087 13.8501C11.6753 13.8751 11.742 13.8834 11.817 13.8834C11.9587 13.8834 12.067 13.8334 12.1587 13.7417L12.8003 13.1084C13.0087 12.9001 13.2087 12.7417 13.4003 12.6417C13.592 12.5251 13.7837 12.4667 13.992 12.4667C14.1503 12.4667 14.317 12.5001 14.5003 12.5751C14.6837 12.6501 14.8753 12.7584 15.0837 12.9001L17.9337 14.8917C18.1503 15.0417 18.3003 15.2167 18.392 15.4251C18.4753 15.6334 18.3087 15.8584 18.3087 15.7751Z" stroke="#3B82F6" strokeWidth="1.5" strokeMiterlimit="10" />
                    </svg>
                  </div>
                </div>
                <div className="inline-flex flex-col justify-start items-start gap-px">
                  <div className="justify-start text-gray-800 text-base font-medium font-['Bricolage_Grotesque']">
                    {formData.candidate_phone || 'Non renseigné'}
                  </div>
                  <div className="self-stretch justify-start text-neutral-400 text-sm font-normal font-['Bricolage_Grotesque']">
                    Téléphone
                  </div>
                </div>
              </div>
              */}
            </div>
            <div className="inline-flex justify-start items-center gap-2">
              <div className="size-9 p-2 bg-orange-400/10 rounded-lg inline-flex flex-col justify-center items-center gap-2.5 overflow-hidden">
                <div data-svg-wrapper className="relative">
                  <svg width="20" height="21" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6.66636 18.8333H13.333C16.683 18.8333 17.283 17.4917 17.458 15.8583L18.083 9.19167C18.308 7.15833 17.7247 5.5 14.1664 5.5H5.83303C2.27469 5.5 1.69136 7.15833 1.91636 9.19167L2.54136 15.8583C2.71636 17.4917 3.31636 18.8333 6.66636 18.8333Z" stroke="#FF7F50" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M6.66699 5.50008V4.83341C6.66699 3.35841 6.66699 2.16675 9.33366 2.16675H10.667C13.3337 2.16675 13.3337 3.35841 13.3337 4.83341V5.50008" stroke="#FF7F50" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M11.6663 11.3333V12.1667C11.6663 12.175 11.6663 12.175 11.6663 12.1833C11.6663 13.0917 11.658 13.8333 9.99967 13.8333C8.34967 13.8333 8.33301 13.1 8.33301 12.1917V11.3333C8.33301 10.5 8.33301 10.5 9.16634 10.5H10.833C11.6663 10.5 11.6663 10.5 11.6663 11.3333Z" stroke="#FF7F50" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M18.042 9.66675C16.117 11.0667 13.917 11.9001 11.667 12.1834" stroke="#FF7F50" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M2.18359 9.8916C4.05859 11.1749 6.17526 11.9499 8.33359 12.1916" stroke="#FF7F50" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              </div>
              <div className="inline-flex flex-col justify-start items-start gap-px">
                <div className="justify-start text-gray-800 text-base font-medium font-['Bricolage_Grotesque']">
                  {formData.target_position}
                </div>
                <div className="self-stretch justify-start text-neutral-400 text-sm font-normal font-['Bricolage_Grotesque']">
                  Position cible
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Documents */}
      <div className="w-full max-w-4xl flex flex-col justify-center items-center gap-4">
        <div className="self-stretch rounded-[10px] outline outline-1 outline-offset-[-1px] outline-neutral-400/20 flex flex-col justify-start items-start overflow-hidden">
          <div className="self-stretch p-4 bg-gray-50 border-b border-neutral-400/20 inline-flex justify-between items-center">
            <div className="justify-start text-gray-800 text-xl font-medium font-['Bricolage_Grotesque']">
              Documents
            </div>
          </div>
          <div className="w-[625px] px-4 py-5 flex flex-col justify-start items-start gap-5">
            <div className="inline-flex justify-start items-center gap-2">
              <div className="size-9 p-2 bg-teal-400/10 rounded-lg inline-flex flex-col justify-center items-center gap-2.5 overflow-hidden">
                <div data-svg-wrapper className="relative">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M15.8333 9.16675C15.8333 8.48508 15.8333 7.85841 15.7067 7.55258C15.58 7.24675 15.3392 7.00508 14.8567 6.52341L10.91 2.57675C10.4942 2.16091 10.2867 1.95341 10.0283 1.83008C9.97487 1.8044 9.92007 1.78159 9.86417 1.76175C9.595 1.66675 9.30083 1.66675 8.71333 1.66675C6.00917 1.66675 4.65667 1.66675 3.74083 2.40508C3.55591 2.5542 3.38745 2.72265 3.23833 2.90758C2.5 3.82508 2.5 5.17591 2.5 7.88008V11.6667C2.5 14.8092 2.5 16.3809 3.47667 17.3567C4.45333 18.3326 6.02417 18.3334 9.16667 18.3334H15.8333M10 2.08341V2.50008C10 4.85675 10 6.03591 10.7325 6.76758C11.4642 7.50008 12.6433 7.50008 15 7.50008H15.4167" stroke="#40E0D0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M17.4997 11.6667H15.833C15.612 11.6667 15.4 11.7545 15.2438 11.9108C15.0875 12.0671 14.9997 12.2791 14.9997 12.5001V13.7501M14.9997 13.7501V15.8334M14.9997 13.7501H17.083M5.83301 15.8334V14.1667M5.83301 14.1667V11.6667H7.08301C7.41453 11.6667 7.73247 11.7984 7.96689 12.0329C8.20131 12.2673 8.33301 12.5852 8.33301 12.9167C8.33301 13.2483 8.20131 13.5662 7.96689 13.8006C7.73247 14.0351 7.41453 14.1667 7.08301 14.1667H5.83301ZM10.4163 11.6667H11.488C12.2772 11.6667 12.9163 12.2884 12.9163 13.0559V14.4442C12.9163 15.2109 12.2763 15.8334 11.488 15.8334H10.4163V11.6667Z" stroke="#40E0D0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              </div>
              <div className="w-40 inline-flex flex-col justify-start items-start gap-px">
                <div className="self-stretch justify-start text-gray-800 text-base font-medium font-['Bricolage_Grotesque']">
                  CV du candidat
                </div>
                <div className="inline-flex justify-start items-center gap-1">
                  <div data-svg-wrapper className="relative">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M2.95967 8.05075C2.82994 8.00788 2.69574 7.97992 2.55967 7.96741L2.46567 7.96341C1.84034 7.96341 1.33301 8.48541 1.33301 9.12875C1.33301 9.71141 1.74834 10.1941 2.29101 10.2807C2.30967 10.2896 2.34012 10.309 2.38234 10.3387C2.56234 10.4687 2.95567 10.8301 3.46434 11.7461C3.66301 12.1047 4.03167 12.3281 4.43301 12.3327C4.70497 12.3359 4.96867 12.2394 5.17434 12.0614M9.99967 3.66675C9.09967 4.01008 8.25167 4.65941 7.51767 5.35341C7.26167 5.59541 7.01101 5.85075 6.76901 6.11275M14.5977 4.42008C14.811 5.01875 14.511 5.42875 13.9323 5.83208C13.4643 6.15741 12.869 6.51008 12.2377 7.11341C11.619 7.70475 11.015 8.41675 10.4783 9.11741C9.82181 9.96596 9.21844 10.8543 8.67167 11.7774C8.57494 11.9474 8.43462 12.0886 8.26517 12.1863C8.09572 12.284 7.90328 12.3348 7.70767 12.3334C7.51189 12.3292 7.32073 12.2731 7.15372 12.1708C6.9867 12.0686 6.8498 11.9239 6.75701 11.7514C6.25834 10.8427 5.87301 10.4841 5.69567 10.3554C5.20434 9.99741 4.66634 9.93541 4.66634 9.15541C4.66634 8.51741 5.16367 8.00008 5.77767 8.00008C6.21634 8.01808 6.61901 8.20608 6.97034 8.46208C7.19834 8.62808 7.44034 8.84741 7.69167 9.13675C7.98634 8.70075 8.34167 8.19808 8.74034 7.67741C9.31901 6.92208 10.0017 6.11075 10.7337 5.41141C11.4537 4.72341 12.287 4.07941 13.169 3.73941C13.7443 3.51741 14.3837 3.82141 14.5977 4.42008Z" stroke="#32CD32" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <div className="justify-start text-green-500 text-xs font-normal font-['Bricolage_Grotesque'] capitalize tracking-tight">
                    Téléchargé
                  </div>
                </div>
              </div>
            </div>
            <div className="inline-flex justify-start items-center gap-2">
              <div className="size-9 p-2 bg-teal-400/10 rounded-lg inline-flex flex-col justify-center items-center gap-2.5 overflow-hidden">
                <div data-svg-wrapper className="relative">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M15.8333 9.16675C15.8333 8.48508 15.8333 7.85841 15.7067 7.55258C15.58 7.24675 15.3392 7.00508 14.8567 6.52341L10.91 2.57675C10.4942 2.16091 10.2867 1.95341 10.0283 1.83008C9.97487 1.8044 9.92007 1.78159 9.86417 1.76175C9.595 1.66675 9.30083 1.66675 8.71333 1.66675C6.00917 1.66675 4.65667 1.66675 3.74083 2.40508C3.55591 2.5542 3.38745 2.72265 3.23833 2.90758C2.5 3.82508 2.5 5.17591 2.5 7.88008V11.6667C2.5 14.8092 2.5 16.3809 3.47667 17.3567C4.45333 18.3326 6.02417 18.3334 9.16667 18.3334H15.8333M10 2.08341V2.50008C10 4.85675 10 6.03591 10.7325 6.76758C11.4642 7.50008 12.6433 7.50008 15 7.50008H15.4167" stroke="#40E0D0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M17.4997 11.6667H15.833C15.612 11.6667 15.4 11.7545 15.2438 11.9108C15.0875 12.0671 14.9997 12.2791 14.9997 12.5001V13.7501M14.9997 13.7501V15.8334M14.9997 13.7501H17.083M5.83301 15.8334V14.1667M5.83301 14.1667V11.6667H7.08301C7.41453 11.6667 7.73247 11.7984 7.96689 12.0329C8.20131 12.2673 8.33301 12.5852 8.33301 12.9167C8.33301 13.2483 8.20131 13.5662 7.96689 13.8006C7.73247 14.0351 7.41453 14.1667 7.08301 14.1667H5.83301ZM10.4163 11.6667H11.488C12.2772 11.6667 12.9163 12.2884 12.9163 13.0559V14.4442C12.9163 15.2109 12.2763 15.8334 11.488 15.8334H10.4163V11.6667Z" stroke="#40E0D0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              </div>
              <div className="w-40 inline-flex flex-col justify-start items-start gap-px">
                <div className="self-stretch justify-start text-gray-800 text-base font-medium font-['Bricolage_Grotesque']">
                  {formData.job_description_file_url ? 'Fiche De Poste' : 'Description du poste'}
                </div>
                <div className="inline-flex justify-start items-center gap-1">
                  <div data-svg-wrapper className="relative">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M2.95967 8.05075C2.82994 8.00788 2.69574 7.97992 2.55967 7.96741L2.46567 7.96341C1.84034 7.96341 1.33301 8.48541 1.33301 9.12875C1.33301 9.71141 1.74834 10.1941 2.29101 10.2807C2.30967 10.2896 2.34012 10.309 2.38234 10.3387C2.56234 10.4687 2.95567 10.8301 3.46434 11.7461C3.66301 12.1047 4.03167 12.3281 4.43301 12.3327C4.70497 12.3359 4.96867 12.2394 5.17434 12.0614M9.99967 3.66675C9.09967 4.01008 8.25167 4.65941 7.51767 5.35341C7.26167 5.59541 7.01101 5.85075 6.76901 6.11275M14.5977 4.42008C14.811 5.01875 14.511 5.42875 13.9323 5.83208C13.4643 6.15741 12.869 6.51008 12.2377 7.11341C11.619 7.70475 11.015 8.41675 10.4783 9.11741C9.82181 9.96596 9.21844 10.8543 8.67167 11.7774C8.57494 11.9474 8.43462 12.0886 8.26517 12.1863C8.09572 12.284 7.90328 12.3348 7.70767 12.3334C7.51189 12.3292 7.32073 12.2731 7.15372 12.1708C6.9867 12.0686 6.8498 11.9239 6.75701 11.7514C6.25834 10.8427 5.87301 10.4841 5.69567 10.3554C5.20434 9.99741 4.66634 9.93541 4.66634 9.15541C4.66634 8.51741 5.16367 8.00008 5.77767 8.00008C6.21634 8.01808 6.61901 8.20608 6.97034 8.46208C7.19834 8.62808 7.44034 8.84741 7.69167 9.13675C7.98634 8.70075 8.34167 8.19808 8.74034 7.67741C9.31901 6.92208 10.0017 6.11075 10.7337 5.41141C11.4537 4.72341 12.287 4.07941 13.169 3.73941C13.7443 3.51741 14.3837 3.82141 14.5977 4.42008Z" stroke="#32CD32" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <div className="justify-start text-green-500 text-xs font-normal font-['Bricolage_Grotesque'] capitalize tracking-tight">
                    {formData.job_description_file_url ? 'Téléchargé' : 'Écrite'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Référents */}
      <div className="w-full max-w-4xl flex flex-col justify-center items-center gap-4">
        <div className="self-stretch rounded-[10px] outline outline-1 outline-offset-[-1px] outline-neutral-400/20 inline-flex flex-col justify-start items-start overflow-hidden">
          <div className="self-stretch p-4 bg-gray-50 border-b border-neutral-400/20 inline-flex justify-between items-center">
            <div className="justify-start text-gray-800 text-xl font-medium font-['Bricolage_Grotesque']">
              Référent(s)
            </div>
          </div>
          <div className="w-[625px] px-4 py-5 flex flex-col justify-start items-start gap-5">
            {formData.selected_referents && formData.selected_referents.length > 0 ? (
              formData.selected_referents.map((referent, i) => (
                <div key={i} className="inline-flex justify-start items-center gap-2">
                  <div className="size-9 p-2 bg-teal-400/10 rounded-lg inline-flex flex-col justify-center items-center gap-2.5 overflow-hidden">
                    <div data-svg-wrapper className="relative">
                      <svg width="20" height="21" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M7.79822 18.7532C7.31361 18.8041 6.82676 18.8309 6.33949 18.8334C5.81965 18.8334 5.69743 18.7957 5.27719 18.7532C4.73354 18.6977 4.32481 18.2532 4.28116 17.7084L3.98473 14.008C3.65775 13.9929 2.96727 13.9762 2.71171 13.9604C2.076 13.9203 1.6014 13.3711 1.67441 12.7381C1.84343 11.2969 2.15759 9.8765 2.61211 8.49846C2.81489 7.88734 3.34227 7.46433 3.98433 7.41115C4.52759 7.36512 5.3014 7.32544 6.33949 7.32544C7.37759 7.32544 8.1514 7.36512 8.69425 7.41076C9.33552 7.46393 9.8633 7.88695 10.0661 8.49806C10.2834 9.15602 10.4687 9.82413 10.6212 10.5M11.9847 12.4842C12.0673 12.0711 12.2327 11.6167 12.3776 11.2651C12.5375 10.8762 12.8724 10.5885 13.2903 10.5389C13.5156 10.5131 13.7421 10.5001 13.9689 10.5C14.2296 10.5 14.4573 10.5163 14.6474 10.5389C15.0653 10.5885 15.4002 10.8762 15.5601 11.2651C15.705 11.6167 15.8704 12.0707 15.953 12.4842" stroke="#40E0D0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M8.9185 4.74611C8.9185 5.4302 8.64674 6.08627 8.16302 6.57C7.67929 7.05372 7.02322 7.32548 6.33913 7.32548C5.65504 7.32548 4.99897 7.05372 4.51524 6.57C4.03152 6.08627 3.75977 5.4302 3.75977 4.74611C3.75977 4.06202 4.03152 3.40595 4.51524 2.92223C4.99897 2.4385 5.65504 2.16675 6.33913 2.16675C7.02322 2.16675 7.67929 2.4385 8.16302 2.92223C8.64674 3.40595 8.9185 4.06202 8.9185 4.74611ZM9.69111 13.7342C9.74627 13.1568 10.1606 12.708 10.7367 12.6386C11.382 12.5604 12.4114 12.4842 13.9689 12.4842C15.5264 12.4842 16.5562 12.5604 17.201 12.6386C17.7772 12.708 18.1915 13.1568 18.2467 13.7342C18.2923 14.2171 18.334 14.8711 18.334 15.6588C18.334 16.4465 18.2923 17.1005 18.2467 17.5834C18.1915 18.1608 17.7772 18.6096 17.201 18.679C16.5558 18.7572 15.5264 18.8334 13.9689 18.8334C12.4114 18.8334 11.3816 18.7568 10.7367 18.679C10.1606 18.6096 9.74627 18.1608 9.69111 17.5834C9.63206 16.9436 9.60293 16.3014 9.60381 15.6588C9.60381 14.8711 9.64548 14.2171 9.69111 13.7342Z" stroke="#40E0D0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                  </div>
                  <div className="inline-flex flex-col justify-start items-start gap-px">
                    <div className="self-stretch justify-start text-gray-800 text-base font-medium font-['Bricolage_Grotesque']">
                      {referent.suggestedRole || 'Manager direct'}
                    </div>
                    <div className="justify-start text-neutral-400 text-sm font-normal font-['Bricolage_Grotesque']">
                      {referent.experienceRef || 'Chef de produit développement marketing chez SHISEIDO'}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-sm text-neutral-400 font-['Bricolage_Grotesque']">
                Aucun référent sélectionné
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Critères d'évaluation */}
      <div className="w-full max-w-4xl flex flex-col justify-center items-center gap-4">
        <div className="self-stretch rounded-[10px] outline outline-1 outline-offset-[-1px] outline-neutral-400/20 inline-flex flex-col justify-start items-start overflow-hidden">
          <div className="self-stretch p-4 bg-gray-50 border-b border-neutral-400/20 inline-flex justify-between items-center">
            <div className="justify-start text-gray-800 text-xl font-medium font-['Bricolage_Grotesque']">
              Critères d'évaluation ({formData.selected_evaluation_criteria?.length || 0})
            </div>
          </div>
          <div className="self-stretch px-4 py-5 flex flex-col justify-start items-start gap-3">
            {formData.selected_evaluation_criteria && formData.selected_evaluation_criteria.length > 0 ? (
              // Affichage des critères par groupes de 3-4 par ligne
              Array.from({ length: Math.ceil(formData.selected_evaluation_criteria.length / 4) }, (_, rowIndex) => (
                <div key={rowIndex} className="inline-flex justify-start items-start gap-3 flex-wrap">
                  {formData.selected_evaluation_criteria
                    .slice(rowIndex * 4, (rowIndex + 1) * 4)
                    .map((criteria: any, index: number) => (
                      <div
                        key={index}
                        className="px-2 py-[5px] bg-gray-50 rounded-lg outline outline-1 outline-offset-[-1px] outline-neutral-400/20 flex justify-center items-center gap-1"
                      >
                        <div className="justify-start text-gray-800 text-base font-normal font-['Bricolage_Grotesque'] capitalize tracking-tight">
                          {criteria.name}
                        </div>
                      </div>
                    ))}
                </div>
              ))
            ) : (
              <div className="w-full text-center py-4 text-neutral-400">
                <div className="text-sm font-['Bricolage_Grotesque']">
                  Aucun critère d'évaluation configuré
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Chronologie et rappels */}
      <div className="w-full max-w-4xl flex flex-col justify-center items-center gap-4">
        <div className="self-stretch rounded-[10px] outline outline-1 outline-offset-[-1px] outline-neutral-400/20 flex flex-col justify-start items-start overflow-hidden">
          <div className="self-stretch p-4 bg-gray-50 border-b border-neutral-400/20 inline-flex justify-between items-center">
            <div className="justify-start text-gray-800 text-xl font-medium font-['Bricolage_Grotesque']">
              Chronologie et rappels
            </div>
          </div>
          <div className="w-[625px] px-4 py-5 flex flex-col justify-start items-start gap-5">
            <div className="inline-flex justify-start items-center gap-2">
              <div className="size-9 p-2 bg-teal-400/10 rounded-lg inline-flex flex-col justify-center items-center gap-2.5 overflow-hidden">
                <div data-svg-wrapper className="relative">
                  <svg width="20" height="21" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M18.3337 10.5001C18.3337 15.1025 14.6027 18.8334 10.0003 18.8334C5.39795 18.8334 1.66699 15.1025 1.66699 10.5001C1.66699 5.89771 5.39795 2.16675 10.0003 2.16675C14.6027 2.16675 18.3337 5.89771 18.3337 10.5001Z" stroke="#40E0D0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M13.0918 13.1583L10.5085 11.575C10.0585 11.3083 9.69183 10.6333 9.69183 10.1083V6.45831" stroke="#40E0D0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              </div>
              <div className="w-40 inline-flex flex-col justify-start items-start gap-px">
                <div className="self-stretch justify-start text-gray-800 text-base font-medium font-['Bricolage_Grotesque']">
                  {formData.campaign_duration === '3_days' ? '3 jours (24h + 48h)' :
                    formData.campaign_duration === '5_days' ? '5 jours (48h + 72h)' :
                      'Non définie'}
                </div>
                <div className="self-stretch justify-start text-neutral-400 text-sm font-normal font-['Bricolage_Grotesque']">
                  Durée de la campagne
                </div>
              </div>
            </div>
            <div className="inline-flex justify-start items-center gap-2">
              <div className="size-9 p-2 bg-yellow-400/10 rounded-lg inline-flex flex-col justify-center items-center gap-2.5 overflow-hidden">
                <div data-svg-wrapper className="relative">
                  <svg width="20" height="21" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M15 7.16675V5.50008C15 3.00008 13.75 2.16675 11.25 2.16675H8.75C6.25 2.16675 5 3.00008 5 5.50008V7.16675C5 9.66675 6.25 10.5001 8.75 10.5001H11.25C13.75 10.5001 15 9.66675 15 7.16675Z" stroke="#FFC506" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M15 13.8334V15.5001C15 18.0001 13.75 18.8334 11.25 18.8334H8.75C6.25 18.8334 5 18.0001 5 15.5001V13.8334C5 11.3334 6.25 10.5001 8.75 10.5001H11.25C13.75 10.5001 15 11.3334 15 13.8334Z" stroke="#FFC506" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M8.125 6.33341H11.875" stroke="#FFC506" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M8.125 14.6667H11.875" stroke="#FFC506" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              </div>
              <div className="inline-flex flex-col justify-start items-start gap-px">
                <div className="justify-start text-gray-800 text-base font-medium font-['Bricolage_Grotesque'] whitespace-nowrap">
                  Rappels automatiques
                </div>
                <div className="self-stretch justify-start text-neutral-400 text-sm font-normal font-['Bricolage_Grotesque']">
                  {formData.reminders_enabled ? '✓ Activé' : 'Désactivé'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Indicateur de progression */}
      {isSubmitting && submissionStep !== 'idle' && (
        <div className="w-full max-w-4xl">
          <div className="p-4 bg-blue-50 rounded-[10px] border border-blue-200">
            <div className="flex items-center gap-3">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
              <div className="text-blue-800 text-sm font-medium font-['Bricolage_Grotesque']">
                {submissionStep === 'validating' && '🔍 Validation des données...'}
                {submissionStep === 'updating' && '📝 Mise à jour du brouillon...'}
                {submissionStep === 'finalizing' && '🚀 Finalisation de la campagne...'}
                {submissionStep === 'sending_email' && '📧 Envoi de l\'email au candidat...'}
                {submissionStep === 'complete' && '✅ Campagne créée avec succès !'}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Boutons de navigation */}
      <div className="w-full max-w-4xl flex justify-between items-center pt-6">
        <Button
          type="button"
          variant="outline"
          onClick={onPrevious}
          disabled={isSubmitting}
          className="px-4 py-2 rounded-lg outline outline-1 outline-offset-[-1px] outline-neutral-400/20 bg-white text-neutral-400 text-sm font-medium font-['Bricolage_Grotesque'] hover:bg-gray-50"
        >
          Précédent
        </Button>

        <Button
          type="button"
          onClick={handleSubmit}
          disabled={isSubmitting || reservationStatus.checking || !reservationStatus.hasValidReservation || reserving || isCheckingRateLimit}
          className="px-6 py-3 bg-teal-400 rounded-lg text-white text-sm font-medium font-['Bricolage_Grotesque'] hover:bg-teal-500 disabled:opacity-50 flex items-center gap-2"
        >
          <Send className="h-4 w-4" />
          {isSubmitting ? 'Lancement...' : 
           reservationStatus.checking ? 'Vérification...' :
           isCheckingRateLimit ? 'Vérification...' :
           !reservationStatus.hasValidReservation ? 'Crédit requis' :
           'Lancer une campagne'}
        </Button>
      </div>
      {/* Modale d'édition des informations du candidat */}
      <EditCandidateModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        currentData={{
          candidate_name: formData.candidate_name || '',
          candidate_email: formData.candidate_email || '',
          candidate_phone: formData.candidate_phone || '',
          target_position: formData.target_position || '',
        }}
        onSave={handleSaveCandidateInfo}
      />

      {/* Modale d'achat de crédits */}
      <BuyCreditModal
        open={isBuyCreditModalOpen}
        onOpenChange={setIsBuyCreditModalOpen}
      />
    </div>
  );
};